/**
 * Network retry utilities backed by p-retry.
 *
 * Exports two retry wrappers and a timing helper:
 * - requestWithRetry: general-purpose retry for any async operation
 * - withRateLimitRetry: selective retry for transient HTTP errors (429/5xx) and network failures
 * - timeFunction: measure wall-clock latency of an async operation
 *
 * Both retry wrappers coerce non-Error rejections into proper Error instances
 * with the original payload preserved as `cause`, so SDKs that throw plain
 * objects (e.g. imagekit-nodejs) don't get replaced by p-retry's opaque
 * `TypeError: Non-error was thrown...` and still flow through retry decisions.
 *
 * Does NOT contain HTTP clients, fetch wrappers, or domain-specific logic.
 * Depends on: p-retry, @/lib/errors/factory (for factory-error detection).
 */
import pRetry from "p-retry";

import { isFactoryError } from "@/lib/errors/factory";

const RETRYABLE_STATUS_CODES = [
  429, // rate limit
  500, // internal server error
  502, // bad gateway
  503, // service unavailable
  504, // gateway timeout
];

const RETRYABLE_NETWORK_ERROR_CODES = new Set([
  "ECONNRESET",
  "ECONNREFUSED",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ESOCKETTIMEDOUT",
  "EPIPE",
  "ENOTFOUND",
  "ENETUNREACH",
  "EHOSTUNREACH",
  "EAI_AGAIN",
]);

function getStatusCode(error: unknown): number | null {
  if (!error || typeof error !== "object") return null;

  if ("statusCode" in error && typeof error.statusCode === "number") {
    return error.statusCode;
  }

  if ("response" in error && error.response && typeof error.response === "object" && "status" in error.response) {
    // cast: axios-style errors nest status under response.status
    const status = (error.response as { status?: unknown }).status;
    if (typeof status === "number") {
      return status;
    }
  }

  return null;
}

function getErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;

  if ("code" in error && typeof (error as { code: unknown }).code === "string") {
    return (error as { code: string }).code;
  }

  return null;
}

function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const directCode = getErrorCode(error);
  if (directCode && RETRYABLE_NETWORK_ERROR_CODES.has(directCode)) return true;

  if ("cause" in error && error.cause && typeof error.cause === "object") {
    const causeCode = getErrorCode(error.cause);
    if (causeCode && RETRYABLE_NETWORK_ERROR_CODES.has(causeCode)) return true;

    if ("cause" in error.cause && error.cause.cause && typeof error.cause.cause === "object") {
      const deepCauseCode = getErrorCode(error.cause.cause);
      if (deepCauseCode && RETRYABLE_NETWORK_ERROR_CODES.has(deepCauseCode)) return true;
    }
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes("network") || msg.includes("socket hang up") || msg.includes("fetch failed")) {
      return true;
    }
  }

  return false;
}

// some sdks (notably imagekit-nodejs) reject with plain objects instead of
// Error instances. p-retry replaces those with an opaque
// `TypeError: Non-error was thrown: "[object Object]"`, dropping all useful
// fields and bypassing isRetryableError. wrap them so the original payload
// survives as `cause` and the existing cause-chasing in isRetryableError can
// still see status codes / network codes nested inside.
function getNonErrorMessage(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  if ("message" in value && typeof (value as { message: unknown }).message === "string") {
    return (value as { message: string }).message;
  }
  return null;
}

function safeStringify(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function coerceToError(value: unknown): Error {
  if (value instanceof Error) return value;
  const message = getNonErrorMessage(value) ?? `Non-error rejection: ${safeStringify(value)}`;
  return new Error(message, { cause: value });
}

function withErrorCoercion<T>(fn: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await fn();
    } catch (error) {
      throw coerceToError(error);
    }
  };
}

function isRetryableError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  if ("cause" in error && error.cause) {
    const causeStatus = getStatusCode(error.cause);
    if (causeStatus !== null) {
      return RETRYABLE_STATUS_CODES.includes(causeStatus);
    }

    const cause = error.cause;
    if (cause && typeof cause === "object" && "cause" in cause) {
      const deepCauseStatus = getStatusCode(cause.cause);
      if (deepCauseStatus !== null) {
        return RETRYABLE_STATUS_CODES.includes(deepCauseStatus);
      }
    }
  }

  const directStatus = getStatusCode(error);
  if (directStatus !== null) {
    return RETRYABLE_STATUS_CODES.includes(directStatus);
  }

  if (isNetworkError(error)) return true;

  return false;
}

/**
 * General-purpose retry for any async operation.
 * Retries all errors (except non-network TypeErrors) with exponential backoff.
 */
async function requestWithRetry<T>(requestFn: () => Promise<T>, maxRetries = 3): Promise<T> {
  return pRetry(withErrorCoercion(requestFn), {
    retries: maxRetries,
    minTimeout: 200,
    factor: 2,
    randomize: true,
  });
}

export { requestWithRetry };

/**
 * Selective retry for transient HTTP errors (429, 500, 502, 503, 504) and
 * network-level failures (ECONNRESET, ETIMEDOUT, DNS errors, etc.).
 * Non-retryable errors and factory errors fail immediately.
 */
export async function withRateLimitRetry<T>(
  fn: () => Promise<T>,
  { maxRetries = 5, baseDelay = 300, maxDelay = 10000 } = {}
): Promise<T> {
  return pRetry(withErrorCoercion(fn), {
    retries: maxRetries,
    minTimeout: baseDelay,
    maxTimeout: maxDelay,
    factor: 2,
    randomize: true,
    shouldRetry: ({ error }) => {
      if (isFactoryError(error)) return false;
      return isRetryableError(error);
    },
  });
}

/**
 * Time a function and return the result and the latency.
 * @example
 * ```typescript
 * const { result, latencyMs } = await timeFunction(async () => {
 *   return await myAsyncOperation(arg1, arg2);
 * });
 * ```
 */
export async function timeFunction<T>(fn: () => Promise<T>): Promise<{ result: T; latencyMs: number }> {
  const start = Date.now();
  const result = await fn();
  const end = Date.now();
  return { result, latencyMs: end - start };
}
